import { t as tr } from './i18n';
import { baseCurrency, curLang, intlLocale, usesLei } from './i18n';

/** Formatări în limba de acum (română: 1.234,5 și „14 mai”; engleză: 1,234.5 și „14 May”). */

export const DAY = 86_400_000;

const fmts = new Map<string, Intl.NumberFormat>();
const fmt = {
  format(n: number): string {
    const loc = intlLocale();
    let f = fmts.get(loc);
    if (!f) {
      f = new Intl.NumberFormat(loc, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
      fmts.set(loc, f);
    }
    return f.format(n);
  },
};

/** Număr în formatul limbii: 1.234,5 (română), 1,234.5 (engleză) */
export const fmtNum = (n: number): string => fmt.format(n);

/** Sumă rotunjită la leu: „1.234 lei” */
/**
 * Sumă rotunjită, în moneda firmei: „1.234 lei”; la o firmă care nu lucrează în lei,
 * „€1,234”, „£1,234” (vezi `baseCurrency`). Numele a rămas de când era doar în lei.
 */
export const lei = (n: number): string => {
  if (usesLei()) return tr('{0} lei', fmt.format(Math.round(n)));
  try {
    return new Intl.NumberFormat(intlLocale(), {
      style: 'currency',
      currency: baseCurrency(),
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(Math.round(n));
  } catch {
    return `${fmt.format(Math.round(n))} ${baseCurrency()}`;
  }
};

/** Data calendaristică de azi, 'AAAA-LL-ZZ', în ora locală. */
export function todayIso(offsetDays = 0): string {
  const d = new Date(Date.now() + offsetDays * DAY);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** 'AAAA-LL-ZZ' → „14 mai” */
export const roDate = (d: string): string =>
  new Date(`${d}T12:00:00`).toLocaleDateString(intlLocale(), {
    day: 'numeric',
    month: 'long',
  });

type Instant = string | number | Date;

const ms = (t: Instant): number =>
  t instanceof Date ? t.getTime() : typeof t === 'number' ? t : new Date(t).getTime();

/** Momentul unei trimiteri, ca zi: „14 mai” */
export const roDateTime = (t: Instant): string =>
  new Date(ms(t)).toLocaleDateString(intlLocale(), { day: 'numeric', month: 'long' });

/** „azi” / „ieri” / „acum 5 zile” */
export function daysAgo(t: Instant): string {
  const d = Math.floor((Date.now() - ms(t)) / DAY);
  if (curLang() === 'en') return d <= 0 ? 'today' : d === 1 ? 'yesterday' : tr('{0} days ago', d);
  return d <= 0 ? tr('azi') : d === 1 ? tr('ieri') : tr('acum {0} zile', d);
}

/** Mărimea unui document: „812 B”, „14 KB”, „1,2 MB”. */
export function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${fmt.format(Math.round((kb / 1024) * 10) / 10)} MB`;
}

/**
 * Numărul scris de utilizator („1.234,5” sau „1234.5”) → număr, sau null. În engleză
 * virgula desparte miile („1,234.5”), deci se scoate; o singură virgulă fără punct
 * („12,5”) rămâne zecimală, cum o scriu și europenii care folosesc engleza.
 */
export function parseNum(value: string): number | null {
  let v = String(value).replace(/\s/g, '');
  if (curLang() === 'en' && (v.includes('.') || (v.match(/,/g) ?? []).length > 1 || /,\d{3}$/.test(v)))
    v = v.replace(/,/g, '');
  const n = parseFloat(v.replace(',', '.'));
  return Number.isNaN(n) ? null : n;
}

/**
 * Numărul pus într-o casetă de editare, ca `parseNum` să-l citească înapoi
 * identic: virgulă zecimală, **fără** separator de mii. `fmtNum(4280)` dă
 * „4.280”, iar `parseNum('4.280')` întoarce 4,28 — deci în casete nu se pune
 * niciodată `fmtNum`, altfel un preț citit corect se salvează de o mie de ori
 * mai mic.
 */
export const numInput = (n: number): string => (curLang() === 'en' ? String(n) : String(n).replace('.', ','));

/**
 * CUI-ul românesc, curățat: fără spații, cu „RO” cu majuscule dacă era scris.
 * „ro 18273645” → „RO18273645”.
 */
export function cleanCui(raw: string): string {
  const s = raw.replace(/\s+/g, '').toUpperCase();
  return s;
}

/**
 * CUI valid după cifra de control (algoritmul ANAF): cifrele fără ultima,
 * aliniate la dreapta pe cheia 753217532, se înmulțesc și se adună; suma × 10
 * modulo 11 (10 devine 0) trebuie să fie ultima cifră. Prinde greșelile de
 * tastare, nu spune dacă firma există.
 */
export function cuiValid(raw: string): boolean {
  const m = /^(RO)?(\d{2,10})$/.exec(cleanCui(raw));
  if (!m) return false;
  const digits = m[2]!;
  const control = Number(digits[digits.length - 1]);
  const body = digits.slice(0, -1).padStart(9, '0');
  const key = '753217532';
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(body[i]) * Number(key[i]);
  const c = ((sum * 10) % 11) % 10;
  return c === control;
}

/**
 * Ce nu e în regulă cu un CUI, ca mesajul să spună exact asta: `format` = nu
 * are forma unui CUI (2–10 cifre, cu sau fără RO); `control` = forma e bună,
 * dar ultima cifră nu se potrivește — de obicei o cifră tastată greșit.
 */
export function cuiProblem(raw: string): '' | 'format' | 'control' {
  if (!/^(RO)?\d{2,10}$/.test(cleanCui(raw))) return 'format';
  return cuiValid(raw) ? '' : 'control';
}
